import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import logoAetna from "@/assets/logo-aetna.svg";
import logoBcbs from "@/assets/logo-bcbs.svg";
import logoCigna from "@/assets/logo-cigna.svg";
import logoUhc from "@/assets/logo-uhc.svg";

const tiers = [
  {
    name: "Initial Consultation",
    price: "Free",
    duration: "15 minutes",
    features: ["Phone or video call", "Discuss your needs", "Determine fit"],
    cta: "Schedule Call",
    href: "https://ashley-bass.clientsecure.me/request/service",
  },
  {
    name: "Individual Session",
    price: "$165",
    duration: "50 minutes",
    features: ["One-on-one therapy", "Personalized treatment plan", "Flexible scheduling"],
    highlighted: true,
    cta: "Book Session",
    href: "https://ashley-bass.clientsecure.me/request/service",
  },
  {
    name: "Family Session",
    price: "$180",
    duration: "75 minutes",
    features: ["Joint therapy session", "Treatment for parents & children", "Communication strategies"],
    cta: "Book Session",
    href: "https://ashley-bass.clientsecure.me/request/service",
  },
];

const insuranceLogos = [
  { src: logoAetna, alt: "Aetna" },
  { src: logoBcbs, alt: "Blue Cross Blue Shield" },
  { src: logoCigna, alt: "Cigna" },
  { src: logoUhc, alt: "United Healthcare" },
];

const PricingSection = () => {
  return (
    <section id="pricing" className="pricing-section py-24 px-6 bg-background">
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-14"
        >
          <h2 className="pricing-heading text-3xl md:text-4xl font-heading font-medium text-foreground mb-4">
            Pricing
          </h2>
          <div className="w-16 h-px bg-accent mx-auto mb-6" />
        </motion.div>

        <div className="pricing-grid grid md:grid-cols-3 gap-6 items-stretch">
          {tiers.map((tier, index) => (
            <motion.div
              key={tier.name}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className={`pricing-card relative rounded-md p-8 border text-center flex flex-col ${
                tier.highlighted
                  ? "pricing-card--highlighted border-primary bg-primary/5 md:scale-105 md:-my-2 shadow-md"
                  : "border-border bg-secondary/50"
              }`}
            >
              {tier.highlighted && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-primary text-primary-foreground text-[10px] font-body font-semibold tracking-widest uppercase rounded-full">
                  Recommended
                </span>
              )}
              <h3 className="pricing-tier-name font-heading font-medium text-foreground text-lg mb-2">{tier.name}</h3>
              <p className="pricing-tier-price text-3xl font-heading font-semibold text-primary mb-1">{tier.price}</p>
              <p className="pricing-tier-duration font-body text-muted-foreground text-sm mb-6">{tier.duration}</p>
              <ul className="space-y-3 text-left mb-8 flex-1">
                {tier.features.map((feature) => (
                  <li
                    key={feature}
                    className="font-body text-sm text-muted-foreground font-light flex items-start gap-2"
                  >
                    <span className="text-primary mt-0.5">•</span>
                    {feature}
                  </li>
                ))}
              </ul>
              <a href={tier.href} target="_blank" rel="noopener noreferrer">
                <Button
                  variant={tier.highlighted ? "default" : "outline"}
                  className="w-full font-body font-medium tracking-wider text-xs uppercase"
                >
                  {tier.cta}
                </Button>
              </a>
            </motion.div>
          ))}
        </div>

        {/* Insurance logos */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mt-14 text-center"
        >
          <p className="font-body text-sm text-muted-foreground mb-5">
            Insurance accepted for Texas residents – your out-of-pocket cost may vary. A limited number of sliding-scale
            spots are available for lower-income clients. Reach out for details.
          </p>
          <div className="flex items-center justify-center gap-10 md:gap-14 flex-wrap">
            {insuranceLogos.map((logo) => (
              <img key={logo.alt} src={logo.src} alt={logo.alt} className="h-7 w-auto opacity-40" />
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default PricingSection;
